import React, { useState } from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  IconButton,
  Chip,
  Button,
  Select,
  MenuItem,
  Skeleton,
  Checkbox
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

function DepartmentTable({
  departments,
  loading,
  onEdit,
  onDelete,
  onView,
  selectedIds = [],
  onSelectionChange
}) {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const startIndex = (page - 1) * rowsPerPage;
  const totalPages = Math.ceil(departments.length / rowsPerPage);

  const paginatedDepartments = departments.slice(
    startIndex,
    startIndex + rowsPerPage
  );

  const allSelected = departments.length > 0 && selectedIds.length === departments.length;

  const isSelected = (departmentId) => selectedIds.includes(departmentId);

  const toggleSelectAll = (checked) => {
    if (checked) {
      onSelectionChange?.(departments.map((department) => department.department_id));
      return;
    }

    onSelectionChange?.([]);
  };

  const toggleSelectOne = (departmentId, checked) => {
    if (checked) {
      onSelectionChange?.([...selectedIds, departmentId]);
      return;
    }

    onSelectionChange?.(selectedIds.filter((id) => id !== departmentId));
  };

  const badgeColor = (name) => {
    const colors = {
      IT: "#dbeafe",
      HR: "#dcfce7",
      Finance: "#ede9fe",
      Admin: "#dbeafe",
      NW: "#fef3c7",
      SW: "#fce7f3"
    };

    return colors[name] || "#f1f5f9";
  };

  return (
    <Box>
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "0 2px 10px rgba(0,0,0,0.05)"
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "background.default" }}>
              <TableCell
                sx={{
                  width: 56,
                  fontWeight: 600,
                  fontSize: 13,
                  color: "#475569"
                }}
              >
                <Checkbox
                  checked={allSelected}
                  indeterminate={selectedIds.length > 0 && !allSelected}
                  onChange={(event) => toggleSelectAll(event.target.checked)}
                />
              </TableCell>

              {[
                "Department Name",
                "Description",
                "Actions"
              ].map((head) => (
                <TableCell
                  key={head}
                  sx={{
                    fontWeight: 600,
                    fontSize: 13,
                    color: "#475569"
                  }}
                >
                  {head}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading && Array.from({ length: rowsPerPage }).map((_, index) => (
              <TableRow key={`department-skeleton-${index}`}>
                <TableCell>
                  <Skeleton variant="circular" width={24} height={24} />
                </TableCell>

                <TableCell>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Skeleton variant="rounded" width={34} height={24} />
                    <Skeleton variant="text" width={140} height={24} />
                  </Box>
                </TableCell>

                <TableCell>
                  <Skeleton variant="text" width="90%" height={24} />
                </TableCell>

                <TableCell>
                  <Box display="flex" gap={1}>
                    <Skeleton variant="circular" width={32} height={32} />
                    <Skeleton variant="circular" width={32} height={32} />
                    <Skeleton variant="circular" width={32} height={32} />
                  </Box>
                </TableCell>
              </TableRow>
            ))}

            {!loading && paginatedDepartments.map((department) => (
              <TableRow key={department.department_id} hover>
                <TableCell>
                  <Checkbox
                    checked={isSelected(department.department_id)}
                    onChange={(event) => toggleSelectOne(department.department_id, event.target.checked)}
                  />
                </TableCell>

                <TableCell>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Chip
                      label={department.department_name.substring(0, 3).toUpperCase()}
                      size="small"
                      sx={{
                        background: badgeColor(department.department_name),
                        fontWeight: 600,
                        fontSize: 12
                      }}
                    />

                    <Typography fontSize={14} fontWeight={600} color="text.primary">
                      {department.department_name}
                    </Typography>

                  
                  </Box>
                </TableCell>

                <TableCell>
                  <Typography fontSize={14} color="#475569">
                    {department.description || "No description"}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Box display="flex" gap={1}>
                    <IconButton
                      onClick={() => onView && onView(department)}
                      sx={{
                        background: "#eff6ff",
                        width: 32,
                        height: 32
                      }}
                    >
                      <VisibilityIcon
                        fontSize="small"
                        sx={{ color: "#2563eb" }}
                      />
                    </IconButton>

                    <IconButton
                      onClick={() => onEdit(department)}
                      sx={{
                        background: "#fff7ed",
                        width: 32,
                        height: 32
                      }}
                    >
                      <EditIcon
                        fontSize="small"
                        sx={{ color: "#f97316" }}
                      />
                    </IconButton>

                    <IconButton
                      onClick={() => onDelete(department)}
                      sx={{
                        background: "#fef2f2",
                        width: 32,
                        height: 32
                      }}
                    >
                      <DeleteIcon
                        fontSize="small"
                        sx={{ color: "#ef4444" }}
                      />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}

            {!loading && paginatedDepartments.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} sx={{ py: 6 }}>
                  <Box
                    sx={{
                      maxWidth: 520,
                      mx: "auto",
                      textAlign: "center",
                      borderRadius: 3,
                      border: "1px solid #ddd6fe",
                      background: "linear-gradient(145deg, #fafaff 0%, #f2edff 100%)",
                      p: 4
                    }}
                  >
                    <Box
                      sx={{
                        width: 58,
                        height: 58,
                        mx: "auto",
                        mb: 1.8,
                        borderRadius: "50%",
                        background: "#ede9fe",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#6d28d9"
                      }}
                    >
                      <ApartmentRoundedIcon sx={{ fontSize: 30 }} />
                    </Box>

                    <Typography variant="h6" sx={{ color: "#4c1d95", fontWeight: 700, mb: 0.7 }}>
                      No departments available
                    </Typography>

                    <Typography sx={{ color: "#5b4b8a", fontSize: 14, mb: 1.8 }}>
                      Create your first department so teams can be organized properly.
                    </Typography>

                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.7,
                        px: 1.4,
                        py: 0.6,
                        borderRadius: 999,
                        background: "#ede9fe",
                        color: "#6d28d9",
                        fontSize: 12,
                        fontWeight: 700
                      }}
                    >
                      <AutoAwesomeRoundedIcon sx={{ fontSize: 14 }} />
                      Structure starts here
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {!loading && departments.length > 0 && (
        <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
          <Typography fontSize={13} color="text.secondary">
            Showing {departments.length === 0 ? 0 : startIndex + 1}
            {" "}
            to
            {" "}
            {Math.min(startIndex + rowsPerPage, departments.length)}
            {" "}
            of {departments.length} results
          </Typography>

          <Box display="flex" alignItems="center" gap={1}>
            <Select
              size="small"
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setPage(1);
              }}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
            </Select>

            <Button
              size="small"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              <ChevronLeftIcon />
            </Button>

            <Button variant="contained" size="small" sx={{ minWidth: 32 }}>
              {page}
            </Button>

            <Button
              size="small"
              disabled={page >= totalPages || totalPages === 0}
              onClick={() => setPage(page + 1)}
            >
              <ChevronRightIcon />
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default DepartmentTable;
